import type { BotAvatarSet } from "@rakazo/contracts";
import { WAFI_AVATAR } from "@rakazo/contracts";
import type { BotColorDef, LogoAvatarActivity } from "@rakazo/core";
import {
  ACTIVE_RUN_STATUSES,
  BOT_AVATAR_COLORS,
  BOT_COLOR_LIST,
  DEFAULT_BOT_AVATAR_COLOR,
  resolvePersonaColorDef,
  SHIPPED_BOT_AVATAR_SHAPE_KEYS,
  SHIPPED_BOT_AVATAR_SHAPES,
  wafiAvatarImage,
} from "@rakazo/core";
import { memo } from "react";
import { useBotAvatarActivity } from "./bot-avatar-activity.js";
import { cn } from "./lib/utils.js";

export type { BotColorDef };
export { BOT_AVATAR_COLORS, BOT_COLOR_LIST, DEFAULT_BOT_AVATAR_COLOR, resolvePersonaColorDef };
export const BOT_SHAPES = SHIPPED_BOT_AVATAR_SHAPES;
export const SHIPPED_SHAPE_KEYS = SHIPPED_BOT_AVATAR_SHAPE_KEYS;
export const BOT_MASCOT_SHAPES = SHIPPED_SHAPE_KEYS.map((key) => BOT_SHAPES[key]);
export function parseBotAvatar(
  rawColor: string,
  _identity?: string,
): {
  color: string;
  shapeIndex?: number;
  isImage: boolean;
  imageUrl?: string;
} {
  if (!rawColor) return { color: "#F97316", isImage: false };
  // Only data: image URLs are rendered. Arbitrary http(s)/blob values in `color`
  // must not become <img src> (SSRF / tracking when other members view the bot).
  if (rawColor.startsWith("data:image/")) {
    return { color: "#F97316", isImage: true, imageUrl: rawColor };
  }
  if (rawColor.includes("::shape_")) {
    const parts = rawColor.split("::shape_");
    const rawShapeIdx = parts[1] ?? "0";
    const parsedShapeIdx = /^\d+$/.test(rawShapeIdx) ? Number(rawShapeIdx) : 0;
    const shapeIdx = Number.isSafeInteger(parsedShapeIdx) ? parsedShapeIdx : 0;
    return {
      color: parts[0] || "#F97316",
      shapeIndex: shapeIdx % SHIPPED_SHAPE_KEYS.length,
      isImage: false,
    };
  }
  return { color: rawColor, isImage: false };
}

const AVATAR_HEX = /^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/;

export function defaultBotAvatarValue(rawColor: string): string {
  const parsed = parseBotAvatar(rawColor);
  if (rawColor && !parsed.isImage && AVATAR_HEX.test(parsed.color)) return parsed.color;
  return DEFAULT_BOT_AVATAR_COLOR;
}

export interface BotAvatarProps {
  color: string;
  avatarSet?: BotAvatarSet;
  activity?: LogoAvatarActivity;
  size?: number;
  status?: string;
  identity?: string;
  className?: string;
}

export const BotAvatar = memo(function BotAvatar(props: BotAvatarProps) {
  return props.activity === undefined ? <LiveWafiAvatar {...props} /> : <WafiAvatar {...props} />;
});
function LiveWafiAvatar(props: BotAvatarProps) {
  const activity = useBotAvatarActivity(props.identity ?? "", props.status);
  return <WafiAvatar {...props} activity={activity} />;
}
/** Offsets each avatar's blink and drift so a roster never moves in lockstep. */
function lifePhase(seed: string): Record<string, string> {
  let h = 0;
  for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) >>> 0;
  return {
    "--wafi-blink-delay": `-${(h % 5200) / 1000}s`,
    "--wafi-drift-delay": `-${((h >>> 8) % 7000) / 1000}s`,
    "--wafi-breathe-delay": `-${((h >>> 16) % 5000) / 1000}s`,
  };
}
function WafiAvatar({ color, identity, size = 36, status, activity, className }: BotAvatarProps) {
  const working =
    ACTIVE_RUN_STATUSES.some((value) => value === status) ||
    (activity !== undefined && activity !== "idle" && activity !== "error");
  return (
    <span
      className={cn("wafi-breathe relative inline-flex shrink-0 select-none", className)}
      data-wafi-avatar="true"
      data-working={working}
      data-activity={activity}
      style={{ width: size, height: size, ...lifePhase(identity ?? color) }}
    >
      <img src={wafiAvatarImage(color, identity)} alt="" className="h-full w-full object-contain" />
      {size >= 20 ? (
        <span aria-hidden="true" className="pointer-events-none absolute inset-0">
          {/* Painted eye centres measured on the 512px source: (230,126), (302,110).
            Sample nearby body texture under each oval so the painted eye closes too. */}
          {[
            { left: "42.2%", top: "21.1%", sample: "45% 31%" },
            { left: "56.3%", top: "18%", sample: "59% 30%" },
          ].map((eye) => (
            <span
              key={eye.left}
              className="absolute rounded-full"
              style={{
                left: eye.left,
                top: eye.top,
                width: "5.5%",
                height: "7%",
                backgroundImage: `url(${wafiAvatarImage(color, identity)})`,
                backgroundSize: "1818% 1428%",
                backgroundPosition: eye.sample,
              }}
            >
              <span className="wafi-eye-drift absolute inset-0">
                <span
                  className="wafi-eye absolute rounded-full"
                  style={{
                    left: "18%",
                    top: "13%",
                    width: "66%",
                    height: "74%",
                    background: "var(--avatar-eye)",
                  }}
                />
              </span>
            </span>
          ))}
        </span>
      ) : null}
      {working ? (
        <span className="absolute bottom-0 right-0 size-2 rounded-full bg-status-working ring-2 ring-background" />
      ) : null}
    </span>
  );
}
export function BotShapePreview({
  identity,
  label,
  color,
  selected,
  onClick,
}: {
  shapeIndex?: number;
  identity?: string;
  label?: string;
  color: string;
  selected?: boolean;
  onClick?: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label ?? color}
      aria-pressed={selected ?? false}
    >
      <BotAvatar color={color} identity={identity} size={32} />
    </button>
  );
}
export function Wordmark({ className }: { className?: string }) {
  return (
    <div className={cn("flex items-center gap-3", className)}>
      <img src={WAFI_AVATAR} alt="" className="size-11 object-contain" />
      <span className="font-[Aeonik,ui-sans-serif] text-[28px] tracking-tight text-foreground">
        Wafi
      </span>
    </div>
  );
}
